import { noop } from 'lodash-es';
import { createContext, useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import isEnumValue from '~utils/isEnumValue';

import { deriveApproveChargesQueries } from './approveChargesQueryParams';
import {
  ChargesException,
  ChargesGrainTab,
  ChargesIssueType,
  ChargesSettledSide,
  ChargesShowOption,
  ChargesView,
  ChargesViewTab,
  DEFAULT_CHARGES_VIEW,
  selectSettledSide as selectViewSettledSide,
  selectShowOption as selectViewShowOption,
  selectViewTab as selectViewViewTab,
  toggleAiApprovedOnly as toggleViewAiApprovedOnly,
  toggleException as toggleViewException,
} from './approveChargesTabs';
import { ChargesGroupBy } from './chargesGrouping';
import { readStoredIssueTypes, writeStoredIssueTypes } from './issueTypesStorage';

interface ExpandAllRequest {
  isExpanded: boolean;
}

type ApproveChargesPageContextType = ReturnType<typeof deriveApproveChargesQueries> &
  ChargesView & {
    expandAllRequest: ExpandAllRequest | null;
    grainTab: ChargesGrainTab;
    groupBy: ChargesGroupBy;
    issueTypes: ChargesIssueType[];
    requestExpandAll: (isExpanded: boolean) => void;
    selectSettledSide: (settledSide: ChargesSettledSide) => void;
    selectShowOption: (showOption: ChargesShowOption) => void;
    selectViewTab: (viewTab: ChargesViewTab) => void;
    setGrainTab: (grainTab: ChargesGrainTab) => void;
    setGroupBy: (groupBy: ChargesGroupBy) => void;
    setIssueTypes: (issueTypes: ChargesIssueType[]) => void;
    toggleAiApprovedOnly: () => void;
    toggleException: (exception: ChargesException) => void;
  };

const ApproveChargesPageContext = createContext<ApproveChargesPageContextType>({
  ...DEFAULT_CHARGES_VIEW,
  expandAllRequest: null,
  grainTab: ChargesGrainTab.LOADS,
  groupBy: ChargesGroupBy.NONE,
  issueTypes: [],
  jobsQuery: {},
  ordersQuery: {},
  requestExpandAll: noop,
  selectSettledSide: noop,
  selectShowOption: noop,
  selectViewTab: noop,
  setGrainTab: noop,
  setGroupBy: noop,
  setIssueTypes: noop,
  toggleAiApprovedOnly: noop,
  toggleException: noop,
});

const GRAIN_TAB_QUERY_PARAM = 'grain';

function ApproveChargesPageContextProvider({ children }: React.PropsWithChildren) {
  const [searchParams, setSearchParams] = useSearchParams();
  const grainTabParam = searchParams.get(GRAIN_TAB_QUERY_PARAM) ?? '';
  const grainTab = isEnumValue(ChargesGrainTab, grainTabParam)
    ? grainTabParam
    : ChargesGrainTab.LOADS;
  const setGrainTab = useCallback(
    (nextGrainTab: ChargesGrainTab) =>
      setSearchParams(
        (paramsNow) => {
          const nextParams = new URLSearchParams(paramsNow);
          nextParams.set(GRAIN_TAB_QUERY_PARAM, nextGrainTab);

          return nextParams;
        },
        { replace: true },
      ),
    [setSearchParams],
  );
  const [groupBy, setGroupBy] = useState(ChargesGroupBy.NONE);
  const [view, setView] = useState<ChargesView>(DEFAULT_CHARGES_VIEW);
  const [issueTypes, setStoredIssueTypes] = useState(readStoredIssueTypes);
  const [expandAllRequest, setExpandAllRequest] = useState<ExpandAllRequest | null>(null);

  const requestExpandAll = useCallback(
    (isExpanded: boolean) => setExpandAllRequest({ isExpanded }),
    [],
  );

  const setIssueTypes = useCallback((nextIssueTypes: ChargesIssueType[]) => {
    setStoredIssueTypes(nextIssueTypes);
    writeStoredIssueTypes(nextIssueTypes);
  }, []);

  const selectViewTab = useCallback(
    (viewTab: ChargesViewTab) =>
      setView((viewNow) => selectViewViewTab(viewNow, viewTab)),
    [],
  );
  const selectSettledSide = useCallback(
    (settledSide: ChargesSettledSide) =>
      setView((viewNow) => selectViewSettledSide(viewNow, settledSide)),
    [],
  );
  const selectShowOption = useCallback(
    (showOption: ChargesShowOption) =>
      setView((viewNow) => selectViewShowOption(viewNow, showOption, issueTypes)),
    [issueTypes],
  );
  const toggleAiApprovedOnly = useCallback(
    () => setView((viewNow) => toggleViewAiApprovedOnly(viewNow)),
    [],
  );
  const toggleException = useCallback(
    (exception: ChargesException) =>
      setView((viewNow) => toggleViewException(viewNow, exception)),
    [],
  );

  const { jobsQuery, ordersQuery } = useMemo(
    () => deriveApproveChargesQueries(view, grainTab),
    [grainTab, view],
  );

  const value = useMemo(
    () => ({
      ...view,
      expandAllRequest,
      grainTab,
      groupBy,
      issueTypes,
      jobsQuery,
      ordersQuery,
      requestExpandAll,
      selectSettledSide,
      selectShowOption,
      selectViewTab,
      setGrainTab,
      setGroupBy,
      setIssueTypes,
      toggleAiApprovedOnly,
      toggleException,
    }),
    [
      expandAllRequest,
      grainTab,
      groupBy,
      issueTypes,
      jobsQuery,
      ordersQuery,
      requestExpandAll,
      selectSettledSide,
      selectShowOption,
      selectViewTab,
      setGrainTab,
      setIssueTypes,
      toggleAiApprovedOnly,
      toggleException,
      view,
    ],
  );

  return (
    <ApproveChargesPageContext.Provider value={value}>
      {children}
    </ApproveChargesPageContext.Provider>
  );
}

export default ApproveChargesPageContext;
export { ApproveChargesPageContextProvider };
